import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzVpbDataService,
  CorporateTaxEntity,
  VpbAangifte, WillekeurigeAfschrijving,
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  Observable,
  shareReplay,
  Subscription,
  tap
} from "rxjs";
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-willekeurig-afschrijven',
  templateUrl: './willekeurig-afschrijven.component.html',
  styleUrls: ['./willekeurig-afschrijven.component.scss'],
  imports: [
    SharedUiButtonModule,
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    CommonModule
  ]
})
export class WillekeurigAfschrijvenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);
  private bedragSubscriptions = new Subscription();

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(data => {
        if (data.aangifte) {
          this.updateForm(data.aangifte);
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public omschrijvingBedragFg = new FormGroup({
    omschrijvingBedrag: new FormArray<
      FormGroup<{
        omschrijving: FormControl<string | null>;
        bedragAfschrijving: FormControl<number | null>;
        boekwaardeEindeBoekkjaar: FormControl<number | null>;
      }>
    >([]),
  });

  get omschrijvingBedrag(): FormArray<FormGroup<
    { omschrijving: FormControl<string | null>;
      bedragAfschrijving: FormControl<number | null>;
      boekwaardeEindeBoekkjaar: FormControl<number | null>;
    }>> {
    return this.omschrijvingBedragFg.controls.omschrijvingBedrag;
  }

  addRij(): void {
    this.omschrijvingBedrag.push(this.createFormGroup());
    this.watchBedragChanges();
  }

  verwijderRij(index: number): void {
    this.omschrijvingBedrag.removeAt(index);
    this.saveForm();
    this.dataService.calculateLatestCorporateTaxReturn().subscribe();
    this.watchBedragChanges();
  }

  createFormGroup(item?: WillekeurigeAfschrijving) {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
      bedragAfschrijving: new FormControl<number | null>(item?.bedragAfschrijving ?? null),
      boekwaardeEindeBoekkjaar: new FormControl<number | null>(item?.boekwaardeEindeBoekkjaar ?? null),
    });
  }

  ngOnInit(): void {
    this.watchBedragChanges();
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  private watchBedragChanges(): void {
    this.bedragSubscriptions.unsubscribe();
    this.bedragSubscriptions = new Subscription();

    this.omschrijvingBedrag.controls.forEach(group => {
      this.bedragSubscriptions.add(
        group.controls.bedragAfschrijving.valueChanges.pipe(
          debounceTime(300),
          distinctUntilChanged(),
          takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
          this.saveForm();
          this.dataService.calculateLatestCorporateTaxReturn().subscribe();
        })
      );
    });
  }

  private updateForm(vpbAangifte: VpbAangifte): void {
    const lijst = vpbAangifte.specificatieWillekeurigeAfschrijving || [];
    const formArray = this.omschrijvingBedragFg.controls.omschrijvingBedrag;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, { emitEvent: false });
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        formArray.at(i).patchValue(lijst[i], { emitEvent: false });
      } else {
        formArray.push(this.createFormGroup(lijst[i]), { emitEvent: false });
      }
    }
  }

  saveForm(): void {
    this.omschrijvingBedragFg.updateValueAndValidity({ emitEvent: false });

    const specificaties: WillekeurigeAfschrijving[] = [];

    this.omschrijvingBedragFg.getRawValue().omschrijvingBedrag.forEach(item => {
      const omschrijving = item.omschrijving?.trim();
      if (
        omschrijving &&
        item.bedragAfschrijving != null &&
        item.boekwaardeEindeBoekkjaar != null
      ) {
        specificaties.push({
          omschrijving,
          bedragAfschrijving: item.bedragAfschrijving,
          boekwaardeEindeBoekkjaar: item.boekwaardeEindeBoekkjaar,
        });
      }
    });

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          specificatieWillekeurigeAfschrijving: specificaties
        },
      };
    });
  }
}
